import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../api/axios';
import toast from 'react-hot-toast';

var safeDateFormat = function (dateStr) {
  if (!dateStr) return '—';
  var parts = dateStr.split('T')[0].split('-');
  var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[parseInt(parts[1], 10) - 1] + ' ' + parseInt(parts[2], 10) + ', ' + parts[0];
};

const AdminPanel = () => {
  var navigate = useNavigate();
  var location = useLocation();
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [toggling, setToggling] = useState(null);
  // ✅ Track which company passwords are visible
  var [visiblePasswords, setVisiblePasswords] = useState({});

  var adminToken = localStorage.getItem('adminToken');
  var adminVerified = (location.state && location.state.adminVerified) || adminToken;

  useEffect(function () {
    if (!adminVerified) {
      toast.error('Admin access required');
      navigate('/login');
      return;
    }
    fetchCompanies();
  }, []);

  var fetchCompanies = async function () {
    try {
      var res = await API.get('/admin/companies', {
        headers: { 'x-admin-auth': adminToken },
      });
      setData(res.data.data);
    } catch (error) {
      toast.error('Failed to load');
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  var handleToggle = async function (companyId) {
    setToggling(companyId);
    try {
      var res = await API.put('/admin/companies/' + companyId + '/toggle', {}, {
        headers: { 'x-admin-auth': adminToken },
      });
      toast.success(res.data.message);
      fetchCompanies();
    } catch (error) {
      toast.error('Failed');
    } finally {
      setToggling(null);
    }
  };

  // ✅ Toggle password visibility for a specific company
  var togglePasswordVisibility = function (companyId) {
    setVisiblePasswords(function (prev) {
      var newState = { ...prev };
      newState[companyId] = !newState[companyId];
      return newState;
    });
  };

  var handleCreateAccount = function () {
    navigate('/signup', { state: { adminVerified: true } });
  };

  var handleLogoutAdmin = function () {
    localStorage.removeItem('adminToken');
    navigate('/login');
  };

  if (!adminVerified) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#25D366' }}></div>
      </div>
    );
  }

  var summary = data ? data.summary : {};
  var companies = data ? data.companies : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Vasify" className="w-40 h-12 rounded-lg object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            <h1 className="text-xl font-bold text-gray-900">
              Vasify <span style={{ color: '#25D366' }}>CRM</span>
              <span className="text-sm font-normal text-gray-500 ml-2">— Admin Panel</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleCreateAccount} className="btn-primary text-sm">+ Create Company</button>
            <button onClick={handleLogoutAdmin} className="btn-secondary text-sm">← Back</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <p className="text-2xl font-bold text-gray-900">{summary.totalCompanies || 0}</p>
            <p className="text-sm text-gray-500">Total Companies</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <p className="text-2xl font-bold" style={{ color: '#25D366' }}>{summary.activeCompanies || 0}</p>
            <p className="text-sm text-gray-500">Active</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <p className="text-2xl font-bold text-red-600">{summary.disabledCompanies || 0}</p>
            <p className="text-sm text-gray-500">Disabled</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <p className="text-2xl font-bold text-blue-600">{summary.totalLeads || 0}</p>
            <p className="text-sm text-gray-500">Total Leads</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <p className="text-2xl font-bold text-purple-600">{summary.totalCustomers || 0}</p>
            <p className="text-sm text-gray-500">Total Customers</p>
          </div>
        </div>

        {/* Companies Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Companies</h2>
          </div>

          {companies.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400">No companies yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Company</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Password</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">WhatsApp ID</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Leads</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Converted</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Customers</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Created</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companies.map(function (company) {
                    var isPasswordVisible = visiblePasswords[company.id] || false;

                    return (
                      <tr key={company.id} className={'hover:bg-gray-50 transition-colors' + (!company.is_active ? ' bg-red-50' : '')}>
                        <td className="px-4 py-4 text-sm text-gray-500">#{company.id}</td>
                        <td className="px-4 py-4">
                          <p className="text-sm font-medium text-gray-900">{company.name}</p>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">{company.email}</td>
                        {/* ✅ Password column with show/hide toggle */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {company.plain_password ? (
                              <>
                                <span className="text-sm font-mono text-gray-700">
                                  {isPasswordVisible ? company.plain_password : '••••••••'}
                                </span>
                                <button
                                  onClick={function () { togglePasswordVisibility(company.id); }}
                                  className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors"
                                  title={isPasswordVisible ? 'Hide' : 'Show'}
                                >
                                  {isPasswordVisible ? '🙈 Hide' : '👁 Show'}
                                </button>
                                <button
                                  onClick={function () {
                                    navigator.clipboard.writeText(company.plain_password);
                                    toast.success('Password copied!');
                                  }}
                                  className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors"
                                  title="Copy"
                                >
                                  📋
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400 italic">Not available</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500 font-mono hidden lg:table-cell">
                          {company.whatsapp_phone_number_id || '—'}
                        </td>
                        <td className="px-4 py-4 text-sm text-center font-medium text-gray-900">{company.total_leads}</td>
                        <td className="px-4 py-4 text-sm text-center font-medium text-green-600 hidden md:table-cell">{company.converted_leads}</td>
                        <td className="px-4 py-4 text-sm text-center font-medium text-purple-600 hidden md:table-cell">{company.total_customers}</td>
                        <td className="px-4 py-4 text-center">
                          {company.is_active ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">✅ Active</span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">🚫 Disabled</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500 hidden lg:table-cell">{safeDateFormat(company.created_at)}</td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={function () { handleToggle(company.id); }}
                            disabled={toggling === company.id}
                            className={
                              'text-xs font-medium py-1.5 px-3 rounded-lg transition-colors disabled:opacity-50 ' +
                              (company.is_active
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200')
                            }
                          >
                            {toggling === company.id ? '...' : company.is_active ? '🚫 Disable' : '✅ Enable'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;